import { UnhandledInReportingError } from '../../../error'
import { LabelCheckAnswer } from './labelCheckAnswer'
import type { LabelCheckQuestion } from './labelCheckQuestion'
import type { Node } from '../../node'
import type { QuestionNodeBase, QuestionNodeInterface } from '../questionNode'

export type LabelCheckQuestionNode = QuestionNodeBase<'labelCheck'> & {
  question: LabelCheckQuestion
  answer?: LabelCheckAnswer
}

const isLabelCheckQuestionNode = (
  node: Node | undefined,
): node is LabelCheckQuestionNode => {
  return node?.type === 'question' && node.questionType === 'labelCheck'
}
type RequireLabelCheckQuestionNode = (
  node: Node | undefined,
) => asserts node is LabelCheckQuestionNode
export const requireLabelCheckQuestionNode: RequireLabelCheckQuestionNode =
  node => {
    if (!isLabelCheckQuestionNode(node)) {
      throw new UnhandledInReportingError(
        `node is not label check question node.`,
        {
          node,
        },
      )
    }
  }
const isInvalidAnswer = (node: LabelCheckQuestionNode): boolean => {
  if (LabelCheckAnswer.isLabelCheckAnswer(node.answer)) {
    return node.answer.isInvalid
  }
  return false
}

const isUnfilled = (node: LabelCheckQuestionNode): boolean => {
  if (!LabelCheckAnswer.isLabelCheckAnswer(node.answer)) {
    return true
  }

  // ラベル検査を選択していない場合、未回答
  if (!node.question.labelInspection) {
    return true
  }

  const isAllLabelInspected = node.question.labelInspection.labelIds?.every(
    labelId => node.answer?.inspectedLabelIds?.includes(labelId),
  )

  // マスタに登録されているラベルのうち、一つでも検査されていない場合、未回答
  if (!isAllLabelInspected) {
    return true
  }

  return false
}

const onSelectLabelInspection = (
  node: LabelCheckQuestionNode,
  labelInspectionId: string,
): LabelCheckQuestionNode => {
  return {
    ...node,
    question: {
      ...node.question,
      labelInspection: {
        id: labelInspectionId,
        numberOfLabels: 0,
      },
    },
    // ラベル検査が選択されたタイミングで回答をリセットする
    answer: LabelCheckAnswer.createAnswer({
      reportLabelInspectionId: crypto.randomUUID(),
    }),
  }
}

const hasNonDefaultAnswer = (node: LabelCheckQuestionNode): boolean => {
  if (node.answer === undefined) {
    return false
  }

  // デフォルト回答の場合numberOfLabelsが0になっている
  if (node.answer.numberOfLabels === 0) {
    return false
  }

  return true
}

const _LabelCheckQuestionNode = {
  isInvalidAnswer,
  isLabelCheckQuestionNode,
  hasNonDefaultAnswer,
  isUnfilled,
  onSelectLabelInspection,
} satisfies QuestionNodeInterface<LabelCheckQuestionNode>

export const LabelCheckQuestionNode: typeof _LabelCheckQuestionNode & {
  requireLabelCheckQuestionNode: RequireLabelCheckQuestionNode
  hasNonDefaultAnswer: (node: LabelCheckQuestionNode) => boolean
  isUnfilled: (node: LabelCheckQuestionNode) => boolean
  onSelectLabelInspection: (
    node: LabelCheckQuestionNode,
    labelInspectionId: string,
  ) => LabelCheckQuestionNode
} = {
  ..._LabelCheckQuestionNode,
  requireLabelCheckQuestionNode,
  isUnfilled,
  onSelectLabelInspection,
}
